import {
  IBackgroundCheckPackageTemplate,
  IExperienceTemplateResource,
  isSubTaskTemplateBlock,
  ITaskTemplate,
  TemplateResourceType
} from '@dadohr/shared/domainTypes/experience';
import { fromPairs } from 'lodash';
import shortid from 'shortid';

// BG-check resources are owned 1:1 by their BACKGROUND_CHECK_INVITE subtask:
// the package picker (BackgroundCheckInvite.tsx writeSelection) mutates the
// resource in place, so two blocks sharing a `data.resourceId` means picking
// a package on one silently rewrites the other's (main-378). The helpers in
// this module keep that invariant across the duplication flows. Kept separate
// from template.ts so they stay unit-testable (template.ts drags in firebase).

/**
 * Build the `resourceIdToNewId` map for a *task-level* duplication AND the
 * list of new BG-check resources the caller must append to
 * `expTpl.data.resources`.
 *
 * Storage-style resources (Google Sheets, OneDrive, SharePoint, Docusign
 * templates, GDocs) are intentionally identity-mapped — duplicated tasks
 * should keep writing to the same connected resource (ch9729). BG-check
 * resources are cloned instead: for each one the duplicated task references,
 * we mint a new resourceId and copy the resource (same package / node
 * snapshot).
 *
 * Whole-experience duplication uses its own remap inside `getDuplicate`
 * and doesn't need this helper.
 */
export const prepareTaskDuplicationResourceMap = (
  task: ITaskTemplate,
  templateResources: IExperienceTemplateResource[]
): {
  resourceIdToNewId: { [oldId: string]: string };
  clonedBgCheckResources: IBackgroundCheckPackageTemplate[];
} => {
  const bgCheckResourceIdsUsedByTask = new Set<string>();
  for (const block of task.blocks) {
    if (
      isSubTaskTemplateBlock(block) &&
      block.type === 'BACKGROUND_CHECK_INVITE' &&
      block.data.resourceId
    ) {
      bgCheckResourceIdsUsedByTask.add(block.data.resourceId);
    }
  }

  const clonedBgCheckResources: IBackgroundCheckPackageTemplate[] = [];
  const resourceIdToNewId = fromPairs(
    templateResources.map((r) => {
      if (
        r.type === TemplateResourceType.BACKGROUND_CHECK_PACKAGE &&
        bgCheckResourceIdsUsedByTask.has(r.resourceId)
      ) {
        const newId = shortid();
        clonedBgCheckResources.push({ ...r, resourceId: newId });
        return [r.resourceId, newId];
      }
      return [r.resourceId, r.resourceId]; // identity — see ch9729
    })
  );

  return { resourceIdToNewId, clonedBgCheckResources };
};

/**
 * Single-resource counterpart of `prepareTaskDuplicationResourceMap`, for the
 * in-task "Duplicate" block action (TaskBuilder `onDuplicateBlock`).
 *
 * Clones the BG-check resource behind `resourceId` under a fresh id. The
 * caller must append the clone to the template's `resources` AND point the
 * duplicated block's `data.resourceId` at it. Returns null when there is
 * nothing to clone: no package picked yet (`resourceId: ''`) or a dangling
 * resourceId (kept verbatim, matching `duplicateTaskTemplate`'s fallback —
 * the picker self-heals those by creating a fresh resource on the next
 * pick).
 */
export const cloneBgCheckResource = (
  resourceToCloneId: string,
  templateResources: IExperienceTemplateResource[]
): IBackgroundCheckPackageTemplate | null => {
  if (!resourceToCloneId) {
    return null;
  }
  const resourceToClone = templateResources.find(
    (r) =>
      r.type === TemplateResourceType.BACKGROUND_CHECK_PACKAGE &&
      r.resourceId === resourceToCloneId
  ) as IBackgroundCheckPackageTemplate | undefined;
  if (!resourceToClone) {
    return null;
  }
  return {
    ...resourceToClone,
    resourceId: shortid()
  };
};
